/**
 * Timer component
 *
 * Description:
 * Renders a circular stopwatch countdown timer for quizzes matching the specified design.
 * The second hand moves counter-clockwise (cuenta regresiva).
 * The pink background turns white second-by-second counter-clockwise, resetting to pink at 12 o'clock.
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classes from './Timer.module.css';

/**
 * Calculates SVG path for white pie slice sector from 12 o'clock counter-clockwise to handAngle
 */
const getCounterClockwiseWhiteSectorPath = (
  cx,
  cy,
  r,
  handAngle
) => {
  if (handAngle <= 0 || handAngle >= 360) return '';
  const rad = (handAngle * Math.PI) / 180;
  const x1 = cx + r * Math.sin(rad);
  const y1 = cy - r * Math.cos(rad);
  const sweptAngle = 360 - handAngle;
  const largeArcFlag = sweptAngle > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${cx} ${
    cy - r
  } A ${r} ${r} 0 ${largeArcFlag} 0 ${x1} ${y1} Z`;
};

const Timer = ({ time, onTimeUp }) => {
  const [secondsLeft, setSecondsLeft] = useState(time);

  useEffect(() => {
    setSecondsLeft(time);
  }, [time]);

  useEffect(() => {
    if (!time || time <= 0) return;

    const interval = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          if (onTimeUp) {
            onTimeUp();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [time, onTimeUp]);

  const formatTime = seconds => {
    if (seconds <= 0) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    const formattedMins = mins < 10 ? `0${mins}` : mins;
    const formattedSecs = secs < 10 ? `0${secs}` : secs;
    return `${formattedMins}:${formattedSecs}`;
  };

  if (!time || time <= 0) {
    return null;
  }

  const displayMinutes = Math.floor(secondsLeft / 60);
  const secInMin = secondsLeft % 60;
  const remSec = secInMin === 0 && secondsLeft > 0 ? 60 : secInMin;
  const handAngle = (remSec % 60) * 6;
  const whiteSectorPath = getCounterClockwiseWhiteSectorPath(
    80,
    85,
    64,
    handAngle
  );
  const tickAngles = [
    0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330
  ];

  return (
    <div className={classes.timerWrapper}>
      <svg
        width="140"
        height="140"
        viewBox="0 0 160 160"
        className={classes.clockSvg}
      >
        {/* Stopwatch Top Stem & Button */}
        <rect
          x="76"
          y="6"
          width="8"
          height="10"
          fill="#282e38"
          rx="1"
        />
        <rect
          x="70"
          y="2"
          width="20"
          height="5"
          fill="#282e38"
          rx="1.5"
        />

        {/* Base Outer Dial Circle (Pink/Red background) */}
        <circle
          cx="80"
          cy="85"
          r="65"
          fill="#f4c3c1"
          stroke="#282e38"
          strokeWidth="4"
        />

        {/* White sector overlay swept counter-clockwise by second hand */}
        {whiteSectorPath && (
          <path d={whiteSectorPath} fill="#ffffff" />
        )}

        {/* Inner Accent Ring */}
        <circle
          cx="80"
          cy="85"
          r="63"
          fill="none"
          stroke="#ffffff"
          strokeWidth="1.5"
          opacity="0.5"
        />

        {/* 12 Ticks around dial */}
        {tickAngles.map(angle => (
          <line
            key={angle}
            x1="80"
            y1="26"
            x2="80"
            y2="32"
            stroke="#282e38"
            strokeWidth="2"
            transform={`rotate(${angle} 80 85)`}
          />
        ))}

        {/* Center Red Hand / Needle (moving counter-clockwise) */}
        <line
          x1="80"
          y1="85"
          x2="80"
          y2="28"
          stroke="#ea3838"
          strokeWidth="2.5"
          strokeLinecap="round"
          transform={`rotate(${handAngle} 80 85)`}
          style={{
            transition:
              handAngle === 0 ? 'none' : 'transform 0.3s linear'
          }}
        />

        {/* Center Pivot Dot */}
        <circle cx="80" cy="85" r="4.5" fill="#282e38" />

        {/* Large Minutes Number */}
        <text
          x="80"
          y="74"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="36"
          fontWeight="900"
          fill="#282e38"
          fontFamily="system-ui, -apple-system, sans-serif"
        >
          {displayMinutes}
        </text>

        {/* "minutos" Subtitle */}
        <text
          x="80"
          y="102"
          textAnchor="middle"
          fontSize="13"
          fontWeight="600"
          fill="#58677d"
          fontFamily="system-ui, -apple-system, sans-serif"
        >
          {displayMinutes === 1 ? 'minuto' : 'minutos'}
        </text>
      </svg>

      {/* Digital Countdown Text below dial */}
      <div className={classes.digitalText}>
        Tiempo restante {formatTime(secondsLeft)}
      </div>
    </div>
  );
};

Timer.propTypes = {
  /**
   * Time in seconds for the countdown timer
   */
  time: PropTypes.number,
  /**
   * Callback fired when timer reaches 0
   */
  onTimeUp: PropTypes.func
};

Timer.defaultProps = {
  time: 0,
  onTimeUp: undefined
};

export default Timer;
